import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { filter, first, switchMap, tap } from 'rxjs';
import { Api } from 'src/app/core/common/api';
import { Auth } from 'src/app/core/common/auth';
import { CoreErrorCodes, HttpError, ICoreUser, IDictionary, IErrorTrackingConfigsDto, InternalError } from 'src/app/core/contracts';
import { isServerError, isUnknownError } from 'src/app/core/helpers/error.handler';
import { isProd } from 'src/app/core/helpers/utils';
import { environment } from 'src/environments/environment';

@Injectable()
export class ErrorTracker {
  private auth = inject(Auth);
  private api = inject(Api);

  configs = {} as IErrorTrackingConfigsDto;
  constructor() {
    Sentry.setTags({
      environment: environment.name,
      version: environment.version,
    });
  }
  init() {
    this.auth.isReady$
      .pipe(
        filter(ready => ready),
        first(),
        switchMap(() => this.auth.user$.pipe(tap(user => this.setUser(user)))),
        switchMap(() => this.api.get<IErrorTrackingConfigsDto>('configs/error-tracking', { handleError: false })) // not relying on Configs service in order to update the error configs whenever the user changes
      )
      .subscribe(configs => {
        this.configs = configs;
      });
  }
  setUser(user?: ICoreUser | null) {
    if (user) {
      Sentry.setUser({
        id: user.sub,
        username: user.preferred_username.toString(),
      });
    } else {
      Sentry.setUser(null);
    }
  }
  trackError<T extends unknown[]>(...args: T) {
    let err = args[0];
    if (!this.shouldTrackError(err)) {
      return;
    }

    if (isUnknownError(err)) {
      // err is instance of Error, just a string or a literal object
      // wrap error, to preserve stacktrace (sentry won't show stacktrace for native errors)
      const message = typeof err === 'string' ? err : ((err['message'] || err['msg'] || 'Custom log') as string);
      const code = err instanceof Error ? CoreErrorCodes.Unknown : CoreErrorCodes.CustomLog;
      const wrapper = new InternalError(message, code, false, ...args);
      try {
        // assign original stack to wrapper.stack so Sentry displays it
        if (err instanceof Error && err.stack) {
          wrapper.stack = err.stack;
        }
      } catch (err) {
        // ignore
      }
      err = wrapper;
    }

    Sentry.withScope(scope => {
      scope.setExtras(this.getErrorDetails(err));
      if (args.length > 1) {
        scope.setExtra('RemainingArgs', args.slice(1));
      }
      // use a more stable fingerprint: include error code and message with type-safe guards
      try {
        const rawCode: unknown =
          err instanceof InternalError
            ? err.code
            : err instanceof HttpError && err.response instanceof HttpErrorResponse && isServerError(err.response.error)
            ? err.response.error.code
            : typeof err === 'object' && err !== null && 'code' in err
            ? (err as { code?: unknown }).code
            : undefined;

        const codeForFingerprint = typeof rawCode === 'string' || typeof rawCode === 'number' ? String(rawCode) : String(CoreErrorCodes.Unknown);

        const rawMessage: unknown =
          err instanceof Error
            ? err.message
            : typeof err === 'string'
            ? err
            : typeof err === 'object' && err !== null && 'message' in err
            ? (err as { message?: unknown }).message
            : undefined;

        const messageForFingerprint = typeof rawMessage === 'string' || typeof rawMessage === 'number' ? String(rawMessage) : 'no-message';

        scope.setFingerprint([codeForFingerprint, messageForFingerprint]);
      } catch (err) {
        // in case of any error, fallback to default fingerprint
      }
      Sentry.captureException(err);
    });
  }
  getErrorDetails(err: unknown): IDictionary<unknown> {
    const details: IDictionary<unknown> = { Code: err['code'] || CoreErrorCodes.Unknown };
    if (err instanceof InternalError) {
      details['InternalErrorDetails'] = err.args;
    } else if (err instanceof HttpError && err.response instanceof HttpErrorResponse) {
      const { options, response } = err;
      const { error, message, ok, name, headers: responseHeaders, ...remainingProps } = response; // explicitly exclude ok, name, headers from remainingProps
      if (isServerError(error)) {
        details['Code'] = error.code;
        details['Message'] = error.message || message;
      }
      details['ServerError'] = error;
      details['HttpErrorResponse'] = remainingProps;

      /*
      const { headers: requestHeaders, params, body } = options;
      details['RequestParameters'] = params;
      details['RequestBody'] = body; // sensitive data
      details['RequestHeaders'] = requestHeaders.keys().map(key => `${key}: ${requestHeaders.get(key)}`); // doesn't include headers like version
      */
    } else {
      details['ErrorDetails'] = err;
    }
    return details;
  }
  shouldTrackError(err) {
    if (!isProd || !err) {
      return false;
    }

    // avoid exceeding the free plan quota by ignoring certain errors
    if (err instanceof InternalError) {
      switch (err.code) {
        case CoreErrorCodes.LocationPermissionDenied:
          // many users don't grant permission
          return false;
        default:
          return this.configs.trackInternalErrors;
      }
    }

    if (err instanceof HttpError && err.response instanceof HttpErrorResponse) {
      switch (err.response.status as HttpStatusCode) {
        case HttpStatusCode.NotFound:
          return false; // add to error tracking configs?
        case HttpStatusCode.BadRequest:
          return this.configs.trackBadRequests;
      }
    }

    return true;
  }
}
